import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import type { PipelineCard, Patient } from '../lib/types';
import { formatDate } from '../lib/format';

const STAGES = [
  { id: 'new', label: 'Novo' },
  { id: 'contacted', label: 'Contatado' },
  { id: 'qualified', label: 'Qualificado' },
  { id: 'scheduled', label: 'Agendado' },
  { id: 'attended', label: 'Atendido' },
] as const;

interface CardWithPatient extends PipelineCard {
  patient: Pick<Patient, 'name' | 'phone_e164' | 'first_seen_source'> | null;
}

export default function Pipeline() {
  const [cards, setCards] = useState<CardWithPatient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    (async () => {
      const { data, error: e } = await supabase
        .from('pipeline_cards')
        .select('*, patient:patients(name, phone_e164, first_seen_source)')
        .is('closed_at', null)
        .order('priority', { ascending: false, nullsFirst: false })
        .order('opened_at', { ascending: false })
        .limit(500);
      if (e) setError(e.message);
      setCards((data ?? []) as unknown as CardWithPatient[]);
      setLoading(false);
    })();
  }, []);

  async function moveCard(card: CardWithPatient, newStage: string) {
    setCards((prev) => prev.map((c) => (c.id === card.id ? { ...c, stage: newStage } : c)));
    const { error: e } = await supabase
      .from('pipeline_cards')
      .update({ stage: newStage })
      .eq('id', card.id);
    if (e) setError(`Falha ao mover card: ${e.message}`);
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Pipeline</h1>
        <div className="text-sm text-slate-500">{cards.length} cards abertos</div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-3 text-sm mb-4">{error}</div>
      )}

      {loading ? (
        <div className="text-slate-400">Carregando…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {STAGES.map((stage) => {
            const stageCards = cards.filter((c) => c.stage === stage.id);
            return (
              <div key={stage.id} className="bg-slate-100 rounded-lg p-3 min-h-[300px]">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-sm font-semibold text-slate-700">{stage.label}</h2>
                  <span className="text-xs text-slate-500">{stageCards.length}</span>
                </div>
                <div className="space-y-2">
                  {stageCards.map((card) => (
                    <div key={card.id} className="bg-white border border-slate-200 rounded-md p-3 shadow-sm">
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={`/pacientes/${card.patient_id}`}
                          className="text-sm font-medium text-brand-700 hover:underline truncate"
                        >
                          {card.patient?.name ?? '(sem nome)'}
                        </Link>
                        {card.priority !== null && card.priority >= 8 && (
                          <span className="text-[10px] uppercase font-semibold text-rose-600">Hot</span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">{card.patient?.phone_e164 ?? '—'}</div>
                      <div className="text-[11px] text-slate-400 mt-1">
                        {card.patient?.first_seen_source ?? 'sem origem'} · aberto {formatDate(card.opened_at)}
                      </div>
                      <select
                        value={card.stage}
                        onChange={(e) => moveCard(card, e.target.value)}
                        className="mt-2 w-full text-xs border border-slate-200 rounded px-2 py-1 bg-slate-50"
                      >
                        {STAGES.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                  {stageCards.length === 0 && (
                    <div className="text-xs text-slate-400 text-center py-4">vazio</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
